/* eslint-disable jsx-a11y/no-static-element-interactions */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import Lottie from 'react-lottie';
import {
  Text,
  RichText,
  withSitecoreContext,
} from '@sitecore-jss/sitecore-jss-react';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import defaultsDeep from 'lodash/defaultsDeep';
import compose from 'lodash/fp/compose';
import Spacer from '../../../../ui/Spacer';
import searchAnimation from '../../../../../assets/animations/search-load-grey.json';
import { Container, Column, Row } from '../../../../../styles/Grid';
import { SafePureComponent } from '../../../../../utils/SafePureComponent';
import { withConsumer } from '../../../../../contexts';
import { SolutionBuilderConsumer } from '../../../../../contexts/SolutionBuilderContext';
import { getGeoLocationData } from '../../../../../services/solutionBuilder';
import * as commonStyles from '../../index.styles';
import * as styles from './index.styles';

const defaultData = {
  GeoNarrowingHeadlineContext: { value: '' },
  GeoNarrowingHeadlineNoContext: { value: '' },
  GeoNarrowingSubtitle: { value: '' },
  GeoNarrowingFootNote: { value: '' },
};

const animationOptions = {
  loop: true,
  autoplay: true,
  animationData: searchAnimation,
};

export class GeoNarrowing extends SafePureComponent {
  history = [];
  fields = defaultsDeep(this.props.fields, defaultData);
  checkboxContainerRef = React.createRef();

  constructor(props) {
    super(props);
    this.state = {
      isScrolled: false,
      Parent: null,
      Children: [],
      CurrentNode: null,
      TotalCount: 0,
      selectedNodes: [],
      blockNarrowDown: false,
      wildCardValue: '',
      language: '',
      loadingData: true,
    };
  }

  componentDidMount() {
    const { sitecoreContext, stateManager, t } = this.props;
    const language = get(sitecoreContext, 'route.itemLanguage', '');
    const initialValues = stateManager.getStepData();
    stateManager.setSubmitFormTrigger(this.onNextStepClicked);
    getGeoLocationData(initialValues.geoLocationContext, language).then(
      response => {
        const results = response.data || [];
        const CurrentNode = results.find(element => element.IsParent) || {
          ItemId: '',
          GeoName: t('BackButton'),
        };
        const Children = this.prepareChildrenList(results);
        const TotalCount = Children.reduce(
          (acc, child) => acc + child.LocationQuantity,
          0,
        );
        const wildCardValue = CurrentNode.ItemId ? CurrentNode.GeoName : '';

        this.setState({
          CurrentNode,
          Children,
          TotalCount,
          wildCardValue,
          language,
          loadingData: false,
        });
      },
    );

    this.checkboxContainerRef.current.addEventListener(
      'scroll',
      this.handleLocationScroll,
    );
  }

  componentWillUnmount() {
    this.checkboxContainerRef.current.removeEventListener(
      'scroll',
      this.handleLocationScroll,
    );
  }

  prepareChildrenList = childrenList => {
    return childrenList.filter(element => {
      element.isDisabled = false;
      element.isChecked = false;

      return !element.IsParent;
    });
  };

  onNextStepClicked = () => {
    const { stateManager } = this.props;
    const { Parent, selectedNodes, CurrentNode, language } = this.state;
    const nodes = CurrentNode.isChecked ? [CurrentNode.ItemId] : selectedNodes;
    stateManager.setStepData({ Parent, selectedNodes: nodes, language });
  };

  handleLocationScroll = debounce(() => {
    const {
      offsetHeight,
      scrollHeight,
      scrollTop,
    } = this.checkboxContainerRef.current;
    const isScrolled = scrollHeight - scrollTop <= offsetHeight + 20;

    this.setState({
      isScrolled,
    });
  }, 50);

  handleCheck = ItemId => {
    const { stateManager } = this.props;
    let { Children } = this.state;
    let { selectedNodes } = this.state;
    Children = Children.map(child => {
      if (child.ItemId === ItemId) {
        child.isChecked = !child.isChecked && selectedNodes.length < 3;
        if (child.isChecked) {
          selectedNodes.push(ItemId);
        } else {
          selectedNodes = selectedNodes.filter(nodeId => nodeId !== ItemId);
        }
      }
      return child;
    });

    if (selectedNodes.length >= 1) {
      stateManager.enableNext();
    } else {
      stateManager.disableNext();
    }

    const blockNarrowDown = selectedNodes.length >= 1;
    this.setState({ Children, blockNarrowDown, selectedNodes });
  };

  handleCurrentNodeCheck = () => {
    const { stateManager } = this.props;
    const { CurrentNode } = this.state;
    const selectedNodes = [];
    let Children = this.state.Children;
    CurrentNode.isChecked = !CurrentNode.isChecked;

    const blockNarrowDown = CurrentNode.isChecked;

    Children = Children.map(child => {
      child.isChecked = CurrentNode.isChecked;
      child.isDisabled = CurrentNode.isChecked;
      if (CurrentNode.isChecked) {
        selectedNodes.push(child.ItemId);
      }
      return child;
    });

    if (selectedNodes.length >= 1) {
      stateManager.enableNext();
    } else {
      stateManager.disableNext();
    }

    this.setState({ CurrentNode, Children, blockNarrowDown, selectedNodes });
  };

  resetNode = node => {
    node.isChecked = false;
    node.isDisabled = false;
    return node;
  };

  onMoreInThisAreaClicked = (e, ItemId) => {
    e.stopPropagation();
    const { language } = this.state;
    this.setState({ loadingData: true });

    getGeoLocationData(ItemId, language).then(response => {
      const results = response.data || [];
      let { CurrentNode, Children, TotalCount } = this.state;
      const Parent = CurrentNode ? this.resetNode({ ...CurrentNode }) : null;
      const blockNarrowDown = false;
      const selectedNodes = [];

      this.history.push({
        CurrentNode: CurrentNode,
        Children: Children,
        TotalCount: TotalCount,
      });

      const nextCurrentNode = results.find(element => element.IsParent);
      const wildCardValue = nextCurrentNode.GeoName;
      Children = this.prepareChildrenList(results);
      TotalCount = Children.reduce(
        (acc, child) => acc + child.LocationQuantity,
        0,
      );
      this.setState(
        {
          isScrolled: false,
          Parent,
          CurrentNode: nextCurrentNode,
          Children,
          TotalCount,
          blockNarrowDown,
          selectedNodes,
          wildCardValue,
          loadingData: false,
        },
        () => {
          this.checkboxContainerRef.current.scrollTop = 0;
        },
      );
    });
  };

  onBackToParentClicked = () => {
    const { CurrentNode, Children, TotalCount } = this.history.pop();
    let Parent =
      this.history.length > 0
        ? this.history[this.history.length - 1].CurrentNode
        : null;

    const blockNarrowDown = false;
    const selectedNodes = [];
    const wildCardValue = CurrentNode.GeoName;
    const isScrolled = false;

    this.setState(
      {
        isScrolled,
        Parent,
        CurrentNode,
        Children,
        TotalCount,
        blockNarrowDown,
        selectedNodes,
        wildCardValue,
      },
      () => {
        this.checkboxContainerRef.current.scrollTop = 0;
      },
    );
  };

  renderNode = child => {
    const { t } = this.props;
    const { blockNarrowDown, CurrentNode } = this.state;
    const showCheckbox =
      (child.LocationQuantity < 100 && CurrentNode.ItemId) ||
      (!child.GeoHasChildren && !CurrentNode.ItemId);
    const clickOnRow =
      !showCheckbox && !blockNarrowDown && child.GeoHasChildren;
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      <div
        css={[styles.geoRowWrapper, clickOnRow && styles.hoverableRow]}
        key={child.ItemId}
        onClick={e =>
          clickOnRow && this.onMoreInThisAreaClicked(e, child.ItemId)
        }
      >
        <div css={[styles.geoRow]}>
          <label
            htmlFor={child.ItemId}
            css={[styles.nodeLabel, showCheckbox ? styles.labelMargins : {}]}
          >
            {showCheckbox ? (
              <React.Fragment>
                <input
                  type="checkbox"
                  id={child.ItemId}
                  name={child.GeoName}
                  onChange={() => this.handleCheck(child.ItemId)}
                  checked={child.isChecked}
                  disabled={child.isDisabled}
                  css={styles.input}
                />
                <span
                  css={[
                    styles.checkboxDecoration,
                    child.isChecked && styles.checkboxDecorationChecked,
                    child.isDisabled && styles.checkboxDecorationDisabled,
                  ]}
                >
                  <i
                    css={[
                      styles.checkboxIcon,
                      child.isChecked && styles.checkboxIconChecked,
                    ]}
                    className="icon-check"
                  />
                </span>
              </React.Fragment>
            ) : null}
            <span css={styles.locationName}>{child.GeoName}</span>
            <span
              css={[
                styles.locationQantity,
                child.isChecked && styles.locationQantityActive,
              ]}
              className="locationQantity"
            >
              {child.LocationQuantity}
            </span>
          </label>
          {child.GeoHasChildren ? (
            <div>
              <button
                className="moreInThisAreaBtn"
                css={styles.moreInThisAreaBtn}
                onClick={e => this.onMoreInThisAreaClicked(e, child.ItemId)}
                disabled={blockNarrowDown}
              >
                <span
                  className="moreInThisAreaLabel"
                  css={styles.moreInThisAreaLabel}
                >
                  {t('MoreInThisArea')}
                </span>
                <i className="icon-chevron" css={styles.moreInChevronIcon}></i>
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  renderBackToParent = () => {
    const { Parent } = this.state;
    if (Parent) {
      return (
        <Row>
          <Column cols={{ sm: 6, md: 7, lg: 10, xl: 8 }} offsets={{ xl: 1 }}>
            <div css={styles.backToParentWpp}>
              <button
                css={styles.backToParentBtn}
                onClick={this.onBackToParentClicked}
              >
                <i className="icon-chevron" css={styles.backIcon}></i>
                <span>{Parent.GeoName}</span>
              </button>
            </div>
          </Column>
        </Row>
      );
    }
  };

  renderCurrentNode = () => {
    const { CurrentNode, TotalCount } = this.state;
    if (CurrentNode && CurrentNode.ItemId) {
      const renderCheckbox = TotalCount < 100;
      return (
        <Row>
          <Column cols={{ sm: 6, md: 7, lg: 10, xl: 8 }} offsets={{ xl: 1 }}>
            <div css={styles.currentNode}>
              <label
                htmlFor={CurrentNode.ItemId}
                css={[
                  renderCheckbox ? styles.nodeLabel : styles.parentLabel,
                  renderCheckbox ? styles.labelMargins : {},
                ]}
              >
                {renderCheckbox ? (
                  <React.Fragment>
                    <input
                      type="checkbox"
                      id={CurrentNode.ItemId}
                      name={CurrentNode.GeoName}
                      onChange={() =>
                        this.handleCurrentNodeCheck(CurrentNode.ItemId)
                      }
                      checked={CurrentNode.isChecked}
                      disabled={CurrentNode.isDisabled}
                      css={styles.input}
                    />
                    <span
                      css={[
                        styles.checkboxDecoration,
                        CurrentNode.isChecked &&
                          styles.checkboxDecorationChecked,
                        CurrentNode.isDisabled &&
                          styles.checkboxDecorationDisabled,
                      ]}
                    >
                      <i
                        css={[
                          styles.checkboxIcon,
                          CurrentNode.isChecked && styles.checkboxIconChecked,
                        ]}
                        className="icon-check"
                      />
                    </span>
                  </React.Fragment>
                ) : null}
                <span css={styles.currentNodeLabel}>{CurrentNode.GeoName}</span>
                <span
                  css={[
                    styles.locationQantity,
                    CurrentNode.isChecked && styles.locationQantityActive,
                  ]}
                  className="locationQantity"
                >
                  {CurrentNode.LocationQuantity}
                </span>
              </label>
            </div>
          </Column>
        </Row>
      );
    }
  };

  renderChildren = () => {
    const { Children, isScrolled, loadingData, Parent } = this.state;
    const displayShadow = Children.length > 3 && isScrolled;

    return (
      <Row>
        <Column
          cols={{ sm: 6, md: 6, lg: 8, xl: 6 }}
          css={styles.shadow(displayShadow)}
          offsets={{ md: 1, lg: 2, xl: 3 }}
        >
          <div
            css={styles.checkboxContainer(Parent)}
            ref={this.checkboxContainerRef}
          >
            {loadingData ? (
              <div css={styles.loader}>
                <Lottie
                  options={animationOptions}
                  isClickToPauseDisabled
                  height={100}
                />
              </div>
            ) : (
              Children.map(child => this.renderNode(child))
            )}
          </div>
        </Column>
      </Row>
    );
  };

  renderHeadline = () => {
    const { t } = this.props;
    const { wildCardValue } = this.state;
    const {
      GeoNarrowingHeadlineContext,
      GeoNarrowingHeadlineNoContext,
      GeoNarrowingSubtitle,
    } = this.fields;

    const renderWithContext =
      GeoNarrowingHeadlineContext && GeoNarrowingHeadlineContext.value;

    let renderValue = GeoNarrowingHeadlineNoContext.value;
    if (renderWithContext && wildCardValue !== t('BackButton')) {
      renderValue = GeoNarrowingHeadlineContext.value.replace(
        '{GeoSolutionName}',
        wildCardValue,
      );
    }

    return (
      <Row>
        <Column cols={{ sm: 6 }}>
          <h1 css={commonStyles.headline}>{renderValue}</h1>
          <p css={[commonStyles.description, styles.geoNarrowingDescription]}>
            <RichText field={GeoNarrowingSubtitle} />
          </p>
        </Column>
      </Row>
    );
  };

  renderFootNote = () => {
    const { GeoNarrowingFootNote } = this.fields;
    const { selectedNodes, CurrentNode } = this.state;
    const notParentSelected =
      !CurrentNode || (CurrentNode && !CurrentNode.isChecked);
    return selectedNodes.length >= 1 && notParentSelected ? (
      <Row>
        <Column
          cols={{ sm: 6, md: 8, lg: 12, xl: 12 }}
          css={styles.footNoteColumn}
        >
          <div css={styles.footNoteWrapper}>
            <p css={styles.footNoteText}>
              <Text field={GeoNarrowingFootNote} />
            </p>
          </div>
        </Column>
      </Row>
    ) : null;
  };

  safeRender() {
    return (
      <Spacer customCss={styles.geoNarrowingSpacer}>
        <Container css={styles.containerCss}>
          {this.renderHeadline()}
          {this.renderBackToParent()}
          {this.renderCurrentNode()}
          {this.renderChildren()}
          {this.renderFootNote()}
        </Container>
      </Spacer>
    );
  }
}

GeoNarrowing.displayName = 'SolutionBuilder/Templates/GeoNarrowing';

GeoNarrowing.defaultProps = {
  t: key => `dict=${key}`,
  fields: defaultData,
};

GeoNarrowing.propTypes = {
  fields: PropTypes.shape({
    GeoNarrowingHeadlineContext: PropTypes.shape({ value: PropTypes.string }),
    GeoNarrowingHeadlineNoContext: PropTypes.shape({ value: PropTypes.string }),
    GeoNarrowingSubtitle: PropTypes.shape({ value: PropTypes.string }),
    GeoNarrowingFootNote: PropTypes.shape({ value: PropTypes.string }),
  }),
};

export default compose(
  withSitecoreContext(),
  withNamespaces(),
  withConsumer(SolutionBuilderConsumer, 'stateManager'),
)(GeoNarrowing);
